import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, ArrowLeft, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ProfileMenu from './ProfileMenu';

// The one app frame for customers and agents: the same sidebar, top bar and content area the admin and
// technical shells use (same CSS classes from index.css, so colours, fonts and spacing match by
// construction), plus a bottom tab bar on phones. Each role passes its own menu; the pages inside
// are untouched.
//   items   sidebar menu: { to, icon, label, end?, badge?, alsoOn? } (alsoOn: other addresses of the same page that keep it highlighted)
//   tabs    phone bottom bar (same shape); omit for no bottom bar
//   title   top-bar title        onBack  shows a back arrow (agent detail pages)
//   topExtras / bell   extra buttons in the top bar     logo / topLogo  sidebar logo / logo shown in the top bar on phones
//   banner  strip under the top bar (e.g. "viewing as agent")     flush  page pads itself (agent screens)
//   hideTitleOnPhone  logos only in the phone top bar (the highlighted tab already says where you are)
export default function RoleShell({
  items = [], tabs = [], title, onBack, topExtras, bell, logo, topLogo, centerLogo = false, banner,
  contentMaxWidth, flush = false, hideTitleOnPhone = false, onLogout, children,
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className={`admin-layout role-shell${tabs.length ? ' shell-has-tabs' : ''}${hideTitleOnPhone ? ' shell-title-desktop' : ''}`}>
      {open && <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 90 }} />}

      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-logo">{logo}</div>

        <nav className="sidebar-nav">
          {items.map(({ to, icon: Icon, label, end, badge, alsoOn }) => (
            <NavLink key={to} to={to} end={end} onClick={close} className={({ isActive }) => `nav-item ${isActive || alsoOn?.includes(pathname) ? 'active' : ''}`}>
              <Icon size={16} />
              {label}
              {badge ? <span className="badge">{badge}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar-circle">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="sidebar-user-info">
              <p>{user?.name}</p>
              <span>{user?.role}</span>
            </div>
          </div>
          <button className="btn-logout" onClick={onLogout}>
            <LogOut size={13} style={{ display: 'inline', marginRight: 6 }} />
            Sign Out
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            {onBack ? (
              <button onClick={onBack} title="Back" style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 6 }}>
                <ArrowLeft size={20} />
              </button>
            ) : (
              <button onClick={() => setOpen(o => !o)} className="mobile-menu-btn" title="Menu"><Menu size={20} /></button>
            )}
            {topLogo && !onBack && <span className={`shell-topbar-logo${centerLogo ? ' center' : ''}`}>{topLogo}</span>}
            <span className="topbar-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          </div>
          <div className="topbar-right">
            {topExtras}
            {bell}
            <ProfileMenu user={user} onLogout={onLogout} size={30} showAppearance={false} />
          </div>
        </header>

        {banner}

        <main className={`page-content${flush ? ' shell-flush' : ''}`} style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}>
          {children}
        </main>
      </div>

      {tabs.length > 0 && (
        <nav className="shell-tabs" aria-label="Main">
          {tabs.map(({ to, icon: Icon, label, end, alsoOn }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive || alsoOn?.includes(pathname) ? 'active' : '')}>
              <Icon size={19} />
              {label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
